import React, { useMemo } from "react";
import { useFormikContext } from "formik";
import Grid from "@mui/material/Grid";

import FieldsIdentifiers from "../../../../../../capture/FieldsIdentifiers";
import { SustainabilitySheetProvider } from "../../../../../../capture/SustainabilitySheetContext";
import FieldsOrigin from "../../../../../../capture/FieldsOrigin";
import FieldsDestination from "../../../../../../capture/FieldsDestination";
import { useFieldSettings } from "../../../../../../../contexts/customFields";
import useMutation from "../../../../../../../hooks/useMutation";
import { ADD_ASSET_IMAGE_MUTATION } from "../../../../../../../constants/graphql";
import InventoryPlacementButton from "./InventoryPlacementButton";
import PickingAndStaging from "./PickingAndStaging";
import ReturnToStorage from "./ReturnToStorage";
import { excludedFields } from "./excludedFields";
import useEnabledFields from "src/views/capture/hooks/useEnabledFields";
import FieldsSection from "../../../../../../capture/FieldsSection";

export default function FormFields({
  type,
  disabledForm,
  colorsOptions = [],
  manufacturersOptions = [],
  asset,
  updateAsset,
  setErrors,
  internalWorkOrderItem,
  internalWorkOrder,
  sameLocation,
  project,
  overrideDisplayFields = {},
  displayType,
  internalWorkOrderId,
  dispositionOptions = [],
}) {
  const displayTypeKey = displayType || (type === "item" ? "receivingSettings" : undefined);
  const [createImage] = useMutation(ADD_ASSET_IMAGE_MUTATION);
  const displayFields = useFieldSettings(project, displayTypeKey);
  const display = useMemo(
    () => ({
      ...displayFields,
      quantity_ordered: false,
      ...overrideDisplayFields,
      ...Object.fromEntries(excludedFields.map(key => [key, false])),
    }),
    [displayFields, overrideDisplayFields]
  );
  const { setFieldValue } = useFormikContext();
  const { orderedSection: enabledFields } = useEnabledFields({
    display,
    type,
    asset,
    onRestockInventory: handleRestockInventory,
    availableQuantityComponent: InventoryPlacementButton,
    sameLocation,
    manufacturersOptions,
    colorsOptions,
    dispositionOptions,
    handleUpdateAssetField,
    project,
  });

  const handleUpdateAssetField = field => (value, { onSuccess, onFailure } = {}) => {
    if (field === "barcode") {
      setFieldValue(field, value);
    }
    updateAsset({
      variables: { id: asset.id, input: { [field]: value } },
      onSuccess,
      onFailure: errors => {
        setErrors(errors);
        onFailure && onFailure(errors);
      },
    });
  };

  const handleRestockInventory = (data, values) => {
    setFieldValue("availableQuantity", data.asset.availableQuantity);
  };

  return (
    <SustainabilitySheetProvider>
      <Grid container spacing={1}>
        <Grid size={12}>
          <FieldsIdentifiers
            fullWidth
            display={display}
            type={type}
            disabledForm={disabledForm}
            asset={asset}
            createImage={createImage}
            handleUpdateAssetField={handleUpdateAssetField}
            internalWorkOrderId={internalWorkOrderId}
          />
        </Grid>
        <Grid size={12}>
          <FieldsSection
            title="Descriptive"
            fullWidth
            fields={enabledFields.descriptive || []}
            disabledForm={disabledForm}
          />
        </Grid>
        <Grid size={12}>
          <FieldsSection
            title="Configurable Fields"
            fullWidth
            fields={enabledFields.configurable || []}
            disabledForm={disabledForm}
          />
        </Grid>
        <Grid size={12}>
          <FieldsSection
            title="Storage"
            fullWidth
            disabledForm={{
              ...disabledForm,
              available_quantity: false,
              rack_locations: false,
              reserved_quantity: false,
            }}
            fields={enabledFields.storage || []}
          />
        </Grid>
        <Grid size={12}>
          <FieldsOrigin
            fullWidth
            display={display}
            asset={asset}
            disabledForm={disabledForm}
            colorsOptions={colorsOptions}
            manufacturersOptions={manufacturersOptions}
          />
        </Grid>
        <Grid size={12}>
          <FieldsDestination
            display={display}
            type={type}
            asset={asset}
            disabledForm={disabledForm}
            colorsOptions={colorsOptions}
            manufacturersOptions={manufacturersOptions}
          />
        </Grid>
        <Grid size={12}>
          <PickingAndStaging
            asset={asset}
            internalWorkOrderItem={internalWorkOrderItem}
            internalWorkOrder={internalWorkOrder}
          />
        </Grid>
        <Grid size={12}>
          <ReturnToStorage
            asset={asset}
            internalWorkOrderItem={internalWorkOrderItem}
            internalWorkOrder={internalWorkOrder}
          />
        </Grid>
      </Grid>
    </SustainabilitySheetProvider>
  );
}
